import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Heart, User, ChevronLeft, ChevronRight } from "lucide-react";
import recognitionsService from "../../services/recognitionsService";
import { useTranslation, useFormat } from "../../i18n";

// Read-only recognition logs, rendered as a Settings card.
const RecognitionLogsCard = () => {
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Note: Backend currently only returns basic data (id, sender_id, recipient_id, message, timestamp, source)
  const { data: recognitions = [], isLoading, isError, error } = useQuery({
    queryKey: ["recognitions", "admin", page],
    queryFn: () => recognitionsService.listAll({
      skip: (page - 1) * pageSize,
      limit: pageSize,
    }),
  });

  return (
    <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
      <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2">
        <Heart className="w-5 h-5 text-purple-600" />
        {t("logs.title")}
      </h2>
      <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
        {t("logs.subtitle")}
      </p>

      <div className="border border-slate-200 rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : isError ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">{t("logs.error_title")}</h3>
            <p className="text-slate-500 mb-4">{error?.message || t("logs.error_body")}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 recognition-gradient recognition-btn text-white rounded-full transition-all"
            >
              {t("common:actions.retry")}
            </button>
          </div>
        ) : recognitions.length === 0 ? (
          <div className="p-12 text-center">
            <Heart className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">{t("logs.empty_title")}</h3>
            <p className="text-slate-500">{t("logs.empty_body")}</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("logs.columns.id")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("logs.columns.date_time")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("logs.columns.sender_id")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("logs.columns.recipient_id")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("logs.columns.message")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("logs.columns.source")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {recognitions.map((recognition) => (
                    <tr key={recognition.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-mono">
                        {recognition.id?.toString().slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {recognition.timestamp ? (
                          <>
                            {fmt.date(recognition.timestamp)}<br />
                            <span className="text-xs text-slate-500">
                              {fmt.date(recognition.timestamp, { timeStyle: "medium" })}
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-400">{t("logs.not_available")}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full recognition-gradient flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-xs font-mono text-slate-600">
                            {recognition.sender_id?.toString().slice(0, 8)}...
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-xs font-mono text-slate-600">
                            {recognition.recipient_id?.toString().slice(0, 8)}...
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <p className="text-sm text-slate-700 line-clamp-2">
                          {recognition.message || <span className="text-slate-400">{t("logs.no_message")}</span>}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                          {recognition.source || t("logs.unknown_source")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recognition cards — mobile only */}
            <div className="md:hidden p-2 space-y-2">
              {recognitions.map((recognition) => (
                <div
                  key={recognition.id}
                  className="border border-slate-200 rounded-2xl p-2.5 space-y-1.5"
                >
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-mono text-slate-500 truncate">
                      {recognition.id?.toString().slice(0, 8)}...
                    </span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-medium flex-shrink-0">
                      {recognition.source || t("logs.unknown_source")}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {recognition.timestamp ? (
                      <>
                        {fmt.date(recognition.timestamp)}
                        {" · "}
                        {fmt.date(recognition.timestamp, { timeStyle: "medium" })}
                      </>
                    ) : (
                      t("logs.no_timestamp")
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-1 min-w-0">
                      <div className="w-5 h-5 rounded-full recognition-gradient flex items-center justify-center flex-shrink-0">
                        <User className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span className="font-mono text-slate-600 truncate">
                        {recognition.sender_id?.toString().slice(0, 6)}...
                      </span>
                    </div>
                    <span className="text-slate-400">→</span>
                    <div className="flex items-center gap-1 min-w-0">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                        <User className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span className="font-mono text-slate-600 truncate">
                        {recognition.recipient_id?.toString().slice(0, 6)}...
                      </span>
                    </div>
                  </div>
                  {recognition.message && (
                    <p className="text-xs text-slate-700 line-clamp-2">{recognition.message}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-slate-200 flex items-center justify-between gap-2">
              <div className="text-xs sm:text-sm text-slate-600">
                {t("logs.page", { page })}
              </div>
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-2 sm:px-3 py-1 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-xs sm:text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("logs.previous")}</span>
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={recognitions.length < pageSize}
                  className="px-2 sm:px-3 py-1 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">{t("logs.next")}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecognitionLogsCard;
